import React, { useState, useEffect, useMemo } from 'react';
import { Search, Briefcase, MapPin, ExternalLink, Loader2, Building2, Sparkles, EyeOff, Check, Filter, X, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Job Search Component
 * - Cross-references with resumeLibrary to show applied status
 * - Allows hiding companies you're not interested in
 * - Filters by company
 */
const JobSearch = ({ authToken, masterResume, candidateProfile, cachedState, onStateChange, resumeLibrary = [], onTailorJob }) => {
  // Initialize from cached state if available
  const [selectedRole, setSelectedRole] = useState(cachedState?.selectedRole || '');
  const [customRole, setCustomRole] = useState(cachedState?.customRole || '');
  const [location, setLocation] = useState(cachedState?.location || '');
  const [includeRemote, setIncludeRemote] = useState(cachedState?.includeRemote ?? true);
  const [suggestedRoles, setSuggestedRoles] = useState([]);
  const [jobs, setJobs] = useState(cachedState?.jobs || []);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchStats, setSearchStats] = useState(cachedState?.searchStats || null);
  const [error, setError] = useState(null);

  // Filter state
  const [companyFilter, setCompanyFilter] = useState('');
  const [hideApplied, setHideApplied] = useState(false);
  const [hiddenCompanies, setHiddenCompanies] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('hiddenCompanies') || '[]');
    } catch {
      return [];
    }
  });

  // Get set of companies user has applied to
  const appliedCompanies = useMemo(() => {
    const companies = new Set();
    resumeLibrary.forEach(app => {
      if (app.company) {
        companies.add(app.company.toLowerCase().trim());
      }
    });
    return companies;
  }, [resumeLibrary]);

  // Get unique companies from job results for filter dropdown
  const uniqueCompanies = useMemo(() => {
    const companies = [...new Set(jobs.map(job => job.company))].sort();
    return companies;
  }, [jobs]);

  // Filter jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      // Hide hidden companies
      if (hiddenCompanies.includes(job.company.toLowerCase().trim())) return false;
      // Filter by company if selected
      if (companyFilter && job.company !== companyFilter) return false;
      // Hide applied if toggle is on
      if (hideApplied && appliedCompanies.has(job.company.toLowerCase().trim())) return false;
      return true;
    });
  }, [jobs, hiddenCompanies, companyFilter, hideApplied, appliedCompanies]);

  // Check if user has applied to a company
  const hasAppliedTo = (company) => {
    return appliedCompanies.has(company.toLowerCase().trim());
  };

  // Hide a company
  const hideCompany = (company, e) => {
    e.preventDefault();
    e.stopPropagation();
    const normalized = company.toLowerCase().trim();
    const updated = [...hiddenCompanies, normalized];
    setHiddenCompanies(updated);
    localStorage.setItem('hiddenCompanies', JSON.stringify(updated));
  };

  // Unhide a company
  const unhideCompany = (company) => {
    const normalized = company.toLowerCase().trim();
    const updated = hiddenCompanies.filter(c => c !== normalized);
    setHiddenCompanies(updated);
    localStorage.setItem('hiddenCompanies', JSON.stringify(updated));
  };

  // Persist state changes to parent
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        jobs,
        searchStats,
        selectedRole,
        customRole,
        location,
        includeRemote,
      });
    }
  }, [jobs, searchStats, selectedRole, customRole, location, includeRemote, onStateChange]);

  // Fetch role suggestions when component mounts or resume changes
  useEffect(() => {
    if (masterResume && authToken) {
      fetchSuggestedRoles();
    }
  }, [masterResume, authToken]);

  const fetchSuggestedRoles = async () => {
    setIsLoadingRoles(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/jobs/suggest-roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          resumeText: typeof masterResume === 'string' ? masterResume : JSON.stringify(masterResume),
          candidateProfile
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch role suggestions');
      }

      const data = await response.json();
      setSuggestedRoles(data.roles || []);
    } catch (err) {
      console.error('Error fetching role suggestions:', err);
      if (window.posthog) {
        window.posthog.captureException(err, { component: 'JobSearch', action: 'fetchRoleSuggestions' });
      }
      // Don't show error for role suggestions - just let user search manually
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const searchJobs = async () => {
    const roleToSearch = selectedRole || customRole;
    if (!roleToSearch.trim()) {
      setError('Please select or enter a role to search for');
      return;
    }

    setIsSearching(true);
    setError(null);
    setJobs([]);

    try {
      const response = await fetch(`${API_URL}/api/jobs/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          role: roleToSearch,
          location,
          includeRemote,
          limit: 50
        })
      });

      if (!response.ok) {
        throw new Error('Failed to search jobs');
      }

      const data = await response.json();
      setJobs(data.jobs || []);
      setSearchStats({
        totalFound: data.totalFound,
        companiesSearched: data.companiesSearched,
        role: data.role
      });
    } catch (err) {
      console.error('Error searching jobs:', err);
      if (window.posthog) {
        window.posthog.captureException(err, { component: 'JobSearch', action: 'searchJobs', role: selectedRole || customRole });
      }
      setError('Failed to search jobs. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setCustomRole('');
  };

  const handleCustomRoleChange = (e) => {
    setCustomRole(e.target.value);
    setSelectedRole(''); // Clear selected when typing custom
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-2.5 sm:gap-3 mb-3 sm:mb-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
            <Search className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading text-lg sm:text-xl font-semibold text-slate-900">Find Open Roles</h2>
            <p className="text-xs sm:text-sm text-slate-600">Search 50+ companies directly</p>
          </div>
        </div>

        {/* Role Suggestions - Compact on mobile */}
        {suggestedRoles.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs sm:text-sm font-medium text-slate-700">Suggested roles</span>
            </div>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {suggestedRoles.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleRoleSelect(suggestion.role)}
                  className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-colors cursor-pointer ${
                    selectedRole === suggestion.role
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
                  }`}
                >
                  {suggestion.role}
                  <span className="hidden sm:inline ml-2 text-xs opacity-70">
                    {Math.round(suggestion.confidence * 100)}%
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {isLoadingRoles && (
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Analyzing your resume for role suggestions...
          </div>
        )}

        {/* Search Form */}
        <div className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
              Role / Job Title
            </label>
            <input
              type="text"
              value={customRole || selectedRole}
              onChange={handleCustomRoleChange}
              placeholder="e.g., Product Manager, Software Engineer"
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 rounded-lg sm:rounded-xl text-sm
                focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">
                Location (optional)
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., San Francisco"
                  className="w-full pl-9 pr-3 py-2.5 sm:py-3 border border-slate-200 rounded-lg sm:rounded-xl text-sm
                    focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-50 rounded-lg sm:rounded-xl cursor-pointer border border-slate-200">
                <input
                  type="checkbox"
                  checked={includeRemote}
                  onChange={(e) => setIncludeRemote(e.target.checked)}
                  className="w-4 h-4 text-primary rounded border-slate-300 focus:ring-primary"
                />
                <span className="text-xs sm:text-sm font-medium text-slate-700">Remote</span>
              </label>
            </div>
          </div>

          {error && (
            <div className="text-xs sm:text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <button
            onClick={searchJobs}
            disabled={isSearching || (!selectedRole && !customRole.trim())}
            className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-primary text-white font-semibold rounded-lg sm:rounded-xl text-sm
              hover:bg-dark transition-colors cursor-pointer
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2"
          >
            {isSearching ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">Searching {searchStats?.companiesSearched || '50+'} companies...</span>
                <span className="sm:hidden">Searching...</span>
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                Search Jobs
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results & Filters */}
      {searchStats && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <span className="text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-900">{filteredJobs.length}</span> of {searchStats.totalFound} jobs
              {hiddenCompanies.length > 0 && (
                <span className="text-slate-400"> ({hiddenCompanies.length} companies hidden)</span>
              )}
            </span>
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Company filter */}
            {uniqueCompanies.length > 1 && (
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-primary cursor-pointer"
              >
                <option value="">All companies</option>
                {uniqueCompanies.map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            )}

            {/* Hide applied toggle */}
            <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={hideApplied}
                onChange={(e) => setHideApplied(e.target.checked)}
                className="w-3.5 h-3.5 text-primary rounded border-slate-300 focus:ring-primary"
              />
              <span className="text-slate-600">Hide applied</span>
            </label>

            {/* Show hidden companies */}
            {hiddenCompanies.length > 0 && (
              <button
                onClick={() => {
                  setHiddenCompanies([]);
                  localStorage.setItem('hiddenCompanies', '[]');
                }}
                className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              >
                Show {hiddenCompanies.length} hidden
              </button>
            )}
          </div>
        </div>
      )}

      {/* Job Cards */}
      {filteredJobs.length > 0 && (
        <div className="space-y-3">
          {filteredJobs.map((job, idx) => {
            const applied = hasAppliedTo(job.company);
            return (
              <div
                key={job.id || idx}
                className={`relative bg-white border rounded-xl p-5 transition-all group ${
                  applied ? 'border-green-200 bg-green-50/30' : 'border-slate-200 hover:border-primary/50 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-primary">{job.company}</span>
                      {applied && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          <Check className="w-3 h-3" />
                          Applied
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-base font-semibold text-slate-900 mb-2">
                      {job.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {job.location}
                      </span>
                      {job.department && job.department !== 'Unknown' && (
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-3.5 h-3.5" />
                          {job.department}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => hideCompany(job.company, e)}
                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer hidden sm:block"
                      title={`Hide all ${job.company} jobs`}
                    >
                      <EyeOff className="w-4 h-4" />
                    </button>
                    {onTailorJob && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onTailorJob({
                            company: job.company,
                            title: job.title,
                            location: job.location,
                            url: job.url,
                            department: job.department
                          });
                        }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-xs sm:text-sm font-semibold rounded-lg hover:bg-dark transition-colors cursor-pointer"
                        title="Tailor your resume for this job"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Tailor</span>
                      </button>
                    )}
                    <a
                      href={job.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-9 h-9 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg flex items-center justify-center transition-colors"
                      title="View job posting"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading State */}
      {isSearching && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
            Searching companies...
          </h3>
          <p className="text-sm text-slate-500">
            Checking open positions across 150+ companies
          </p>
        </div>
      )}

      {/* Empty State - only show when NOT searching */}
      {!isSearching && searchStats && filteredJobs.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
            {jobs.length === 0 ? 'No jobs found' : 'All jobs filtered out'}
          </h3>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            {jobs.length === 0
              ? 'Try a different role title or broaden your location preferences.'
              : 'Try adjusting your filters or showing hidden companies.'}
          </p>
          {jobs.length > 0 && (hiddenCompanies.length > 0 || hideApplied || companyFilter) && (
            <button
              onClick={() => {
                setCompanyFilter('');
                setHideApplied(false);
              }}
              className="mt-4 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors cursor-pointer"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default JobSearch;
